import {
  isRetailCodeScan,
  scanGtinMiddle12,
  toPickVerifyUnit,
  type PickVerifyUnitInput,
  type ProductScanLike
} from '@/lib/scanning/retailCode';
import { matchUniqueSerialCaseInsensitive } from '@/lib/scanning/serialMatch';
import { formatDateOnly } from '@/lib/utils/dateUtils';
import { verifyOverScanMessage, verifyRefusalTitle } from './verifyScreen';

/**
 * The Verify screen's scan triage and its serial mutation queue (design §11.8) — pure, so every
 * rule below is pinned by verifyScanQueue.test.ts instead of by pointing a camera at a pack.
 *
 * WHY THE CLIENT DECIDES ANYTHING AT ALL: the server is the authority (it refuses not-on-order,
 * over-scan and duplicate serials inside the transaction), but a continuous-scan screen sees the
 * SAME unit many times — the camera re-arms 250 ms after every read with the pack still in frame,
 * a trigger pull can repeat while the first mutation is in flight — and answering each of those
 * with a refusal toast and a fail tone would teach the operator to ignore both. The camera decodes
 * silently here (SharedScanner `silentDecode`), so a read makes only the page's sounds: the success
 * tone (and, for a camera read, Scandit's own beep) the moment a read is counted, the fail tone
 * when it is refused, nothing when it is ignored.
 * So, in this exact order:
 *
 *  1. REMOVE mode skips the duplicate / hint / over-scan rules below: each read is a deliberate
 *     "take this off", and the unit being removed is by definition one whose serial IS in the list
 *     (rule 3 would refuse it). It keeps ONE rule from Add mode — the camera's sliding re-read
 *     window (rule 2) — because a bottle held in frame would otherwise be removed again on every
 *     re-arm, and bulk units carry no serial to tell one read from the next. Trigger pulls and
 *     manual entries are never suppressed. The job carries its mode from enqueue time, so toggling
 *     Add/Remove while jobs are queued never changes what a queued job does.
 *  2. ADD mode keys a read as `middle12|serial` (the GTIN middle-12 from the GS1 `gtin`, else from
 *     the retail digits; a code with neither keys on its text). A CAMERA read is IGNORED SILENTLY
 *     when the camera read the same key inside that key's SLIDING window: every re-read refreshes
 *     the window, so a pack left in frame stays ignored for as long as the camera keeps seeing it.
 *     The window is kept PER KEY — the last few codes the camera read, each with its own — not for
 *     the last read alone: the camera takes one code per read, and with two in view (an Rx pack's
 *     DataMatrix beside its linear NDC-UPC, another bottle in the tote) it takes them in turns, so
 *     a window on the last read would count the bottle held up again on every turn. How long the
 *     window is depends on what the key can tell apart:
 *       - a key WITH a serial names one pack, so 2.5 s only ever silences re-reads of that pack —
 *         the next pack carries another serial and counts the moment the camera sees it;
 *       - a key WITHOUT one (a BULK / UPC bottle, any linear code) is shared by every unit of the
 *         product, so the window is what separates one unit from the next: 1 s. The camera gesture
 *         for OTC is "present the bottle, take it away for about a second, present the next". A
 *         bottle whose code drops out for longer than that (glare, a turned label) and comes back
 *         counts twice — accepted, because every count is seen and heard as it lands, the device
 *         and the server refuse anything beyond what the order needs, and Remove takes one off.
 *         The same holds when three or more codes take turns in view: each read costs the 250 ms
 *         re-arm plus the decode, so the camera can come back to a bottle less often than once a
 *         second, and it counts again — seen, heard, capped and corrected exactly the same way.
 *     Only camera reads anchor the window — a trigger pull is deliberate. A key already queued /
 *     in flight is ignored too, but only a key that names ONE unit: a serial (a second pull on the
 *     same pack) or a typed entry (a double-tapped Submit). A serial-less read that got this far is
 *     one more bottle — hardware and camera alike, exactly as Remove sends it.
 * 2b. LOCKED: a read (Add or Remove, past rule 2's window) of a group this operator may not
 *     verify — a controlled (CI–CV) product without "Fulfill Controlled Sales Order"
 *     (`controlledLocked`) — is refused on the device in the server's own words, with no call. The
 *     group stays listed and counted; somebody holding the permission scans it.
 *  3. A serial already in the resolved group's `serialNumbers` → "Duplicate serial", no call.
 *     (Reached by a hardware read directly — a trigger pull is deliberate — and by a camera read
 *     only once the window has lapsed, i.e. the operator brought the pack back.)
 *  4. A linear retail read (UPC / EAN — no serial possible) of a SERIALIZED group → a soft hint to
 *     scan the 2D code: a warning without the fail tone, rate-limited, no call. Rx packs carry the
 *     linear NDC-UPC next to the DataMatrix, and the PRODUCT profile decodes both. Any OTHER unit
 *     without a serial for a SERIALIZED group (a typed product code, a GS1 label with no serial)
 *     is not the device's to count — the server refuses it — so it goes to the server WITHOUT its
 *     group, like rule 6: nothing on the row and no sound until the server answers. Remove mode
 *     sends a serial-less read of a SERIALIZED group the same way (the server takes one off by
 *     product code only when bulk units of it were picked).
 *  5. OVER-SCAN: an Add beyond what the group still needs — counting the units still saving
 *     (`pendingUnits`) — is refused on the device in the server's own words, with no call.
 *  6. Anything that resolves to no group goes to the server, which names the product in its
 *     not-on-order refusal (it can look the code up in the whole catalog; the client cannot).
 *  7. LOT: a BULK / UPC unit the device counts is recorded against the stock balance it really
 *     came from (SalesOrderVerifyLine.lots) — last in Add mode, so a full line is refused (rule 5)
 *     rather than asked about, and after rule 1 in Remove mode. Add offers the balances with units
 *     available, Remove the lots with units verified, both net of what is still saving
 *     (verifyLotOptions). None (an older server, or no stock at all) → sent without a balance and
 *     the server picks as it always has. Exactly one → sent with it, nothing asked. A label that
 *     names exactly one of several (a GS1 case label's lot + expiration) whose lot holds the
 *     quantity → sent with that one, nothing asked either. Otherwise `choose-lot`: the page's lot
 *     chooser, opened on the server's suggested row. Nothing is counted until the operator
 *     confirms, and each confirmation is its own job (verifyLotChoiceJob) that
 *     goes AROUND this triage: it is a tap on the page's own sheet, not a read, and both guards of
 *     rule 2 would drop it — the camera window and the in-flight key are `middle12|serial`, which
 *     every confirmation of one product shares (18 of lot ABC then 6 of lot XYZ, or two deliberate
 *     taps on the same lot). The chooser checks each one instead: no more than the line still
 *     needs, no more than that lot holds. Reads keep coming while it is open
 *     (verifyReadDuringLotChoice): another read of its own line adds to the quantity waiting for a
 *     lot — never dropped unheard — and any other product is triaged as usual.
 *
 * A read queued WITH its group is COUNTED at once: the page plays the success tone and shows the
 * row's count with it (withPendingUnits); the server's answer only confirms it, or rolls it back
 * with its refusal. One queued without a group is counted and sounded only by the server's answer,
 * and a `choose-lot` read only by its confirmations.
 */

/** How long a camera re-read of a SERIAL-carrying key counts as "still in frame" (rule 2). */
export const VERIFY_CAMERA_SERIAL_REREAD_WINDOW_MS = 2500;
/** …and of a serial-less key (BULK / UPC, linear codes): the OTC "take it away" gap (rule 2). */
export const VERIFY_CAMERA_SERIAL_LESS_REREAD_WINDOW_MS = 1000;
/** At most one "scan the 2D barcode" hint per this interval. */
export const VERIFY_SOFT_HINT_INTERVAL_MS = 4000;

export type VerifyMode = 'add' | 'remove';

/** `manual` = the page's own entry form; never subject to the camera window. */
export type VerifyScanSource = 'camera' | 'hardware' | 'manual';

/** A SalesOrderVerifyLot: one stock balance (zone + lot + expiration) a BULK / UPC line can use. */
export type VerifyLotLike = {
  balanceId: string;
  zone?: { id?: string | null; name?: string | null } | null;
  lot?: string | null;
  /** An MDate (ISO instant); its UTC calendar date is the one on the label. */
  expiration?: string | null;
  /** Eligible units in the balance now (0 for a lot listed only because units of it are verified). */
  available: number;
  /** Units of this lot already verified on the order. */
  verified: number;
  /** What the server takes without a balanceId (the Pick-confirmed lot, else FEFO). */
  suggested?: boolean | null;
};

/** What the triage reads of a SalesOrderVerifyLine. */
export type VerifyGroupLike = {
  key: string;
  /** SERIALIZED | BULK | UPC */
  kind: string;
  productName?: string | null;
  required: number;
  scanned: number;
  serialNumbers?: readonly string[] | null;
  matchKeys?: readonly string[] | null;
  orphan?: boolean | null;
  /** A controlled product this operator may not verify (rule 2b). Missing from an older server. */
  controlledLocked?: boolean | null;
  deaSchedule?: string | null;
  /** Always [] for SERIALIZED groups — and missing from an older server's list (rule 7). */
  lots?: readonly VerifyLotLike[] | null;
};

/** A ScanResult, or the ad-hoc manual-entry shape SharedScanner hands to `handleScan`. */
export type VerifyScanLike = NonNullable<ProductScanLike> & { source?: string | null };

/** A code the camera read, and when it last read it (rule 2). */
export type VerifyCameraRead = { key: string; at: number };

export type VerifyScanGateState = {
  /**
   * The codes the CAMERA read lately, oldest first (Remove-mode keys are prefixed) — each one
   * anchors its own sliding re-read window.
   */
  recentCameraReads: readonly VerifyCameraRead[];
  /** When the last soft hint was shown (rule 4 rate limit). */
  lastHintAt: number | null;
};

export const initialVerifyScanGateState = (): VerifyScanGateState => ({
  recentCameraReads: [],
  lastHintAt: null
});

/**
 * At most this many codes are remembered at once (rule 2) — more than can take turns in view within
 * a window anyway; it only bounds the state.
 */
export const VERIFY_CAMERA_RECENT_READS_MAX = 8;

/** Remove-mode camera reads anchor the window under this prefix (see rule 1). */
const REMOVE_KEY_PREFIX = 'remove:';

/**
 * The sliding re-read window of a camera anchor key (rule 2). The serial is the key's last part
 * (Remove keys only add a prefix), so a key ending in the separator carries none.
 */
export const verifyCameraRereadWindowMs = (key: string): number =>
  key.endsWith('|')
    ? VERIFY_CAMERA_SERIAL_LESS_REREAD_WINDOW_MS
    : VERIFY_CAMERA_SERIAL_REREAD_WINDOW_MS;

/** Whether the camera read `key` inside its own window — whatever it read in between. */
const cameraStillSees = (state: VerifyScanGateState, key: string, now: number) =>
  !!matchUniqueVerifyScanKey(
    state.recentCameraReads.filter(
      (read) => now - read.at <= verifyCameraRereadWindowMs(key)
    ),
    key,
    (read) => read.key
  );

/**
 * The gate after the camera read `key` at `now`: its window restarts (sliding), every other code
 * keeps its own until it lapses, and only the newest few are kept.
 */
const withCameraRead = (
  state: VerifyScanGateState,
  key: string,
  now: number
): VerifyScanGateState => {
  const liveReads = state.recentCameraReads.filter(
    (read) => now - read.at <= verifyCameraRereadWindowMs(read.key)
  );
  const prior = matchUniqueVerifyScanKey(liveReads, key, (read) => read.key);
  return {
    ...state,
    recentCameraReads: [
      ...liveReads.filter((read) => read !== prior),
      { key, at: now }
    ].slice(-VERIFY_CAMERA_RECENT_READS_MAX)
  };
};

/**
 * The gate after a deliberate Add / Remove switch. Add → Remove clears the camera window: the next
 * read is meant, and taking off the extra unit still in frame is exactly what the switch is for.
 * Remove → Add does NOT: the packs just removed are usually still under the camera, and their next
 * re-reads would otherwise be verified straight back on (with the success tone). So every Remove
 * read still inside its window carries over as an Add read — prefix dropped, window restarted
 * now. A unit is added again only once it has left the frame (its sliding window lapses), or
 * through a deliberate trigger pull or manual entry.
 */
export const verifyGateAfterModeSwitch = (
  state: VerifyScanGateState,
  next: VerifyMode,
  now: number
): VerifyScanGateState => ({
  ...state,
  recentCameraReads:
    next === 'add'
      ? state.recentCameraReads
          .filter(
            (read) =>
              read.key.startsWith(REMOVE_KEY_PREFIX) &&
              cameraStillSees(state, read.key, now)
          )
          .map((read) => ({ key: read.key.slice(REMOVE_KEY_PREFIX.length), at: now }))
      : []
});

/**
 * The gate as the camera comes back after a prompt that covered it (the quarantine stock sheet):
 * the Add read of the unit the prompt was about counts as seen just now. The operator is usually
 * still holding it under the camera — stocked and verified (a re-read would be a "Duplicate"), or
 * left in quarantine (a re-read would open the prompt again) — so it stays silently ignored for as
 * long as it stays in frame, like any pack still in view (rule 2). A trigger pull still reads it.
 */
export const verifyGateWithUnitSeen = (
  state: VerifyScanGateState,
  unit: PickVerifyUnitInput,
  now: number
): VerifyScanGateState => withCameraRead(state, verifyScanKey(unit), now);

export type VerifyScanJob = {
  /**
   * `middle12|serial` for Add reads (in-flight suppression); null for Remove jobs and lot-chooser
   * confirmations (rule 7), which no later read may be mistaken for.
   */
  dedupeKey: string | null;
  mode: VerifyMode;
  unit: PickVerifyUnitInput;
  /**
   * The group the client resolved the read to and counts it towards; null → the server resolves
   * (or refuses) it, and only its answer counts and sounds the read.
   */
  groupKey: string | null;
  source: VerifyScanSource;
  /**
   * The id the write is sent with (newVerifyClientScanId), given by the queue at enqueue. Kept for
   * every re-send of this job, so the server can tell a re-sent request from a new scan.
   */
  clientScanId?: string;
};

export type VerifyIgnoreReason =
  | 'empty'
  | 'paused'
  | 'camera-reread'
  | 'in-flight'
  | 'hint-rate-limited';

export type VerifyScanDecision =
  | { type: 'ignore'; reason: VerifyIgnoreReason }
  | {
      type: 'duplicate';
      groupKey: string;
      serialNumber: string;
      title: string;
      message: string;
    }
  | { type: 'hint'; groupKey: string; title: string; message: string }
  | { type: 'over-scan'; groupKey: string; title: string; message: string }
  | { type: 'locked'; groupKey: string; title: string; message: string }
  | { type: 'enqueue'; job: VerifyScanJob }
  | { type: 'choose-lot'; choice: VerifyLotChoice };

/** A read of a BULK / UPC group with more than one lot to take it from (or put it back to). */
export type VerifyLotChoice = {
  mode: VerifyMode;
  groupKey: string;
  /** The unit as the triage built it; every confirmation sends it with its balance + quantity. */
  unit: PickVerifyUnitInput;
  /** Where the read came from: a typed entry's chooser opens inside the entry form. */
  source: VerifyScanSource;
  /** The row the chooser opens on (verifyLotPreselect). */
  balanceId: string | null;
  /** The units the read asked for: 1 per scan, or the typed quantity. */
  quantity: number;
};

/**
 * Net units still saving per group key — adds minus removes (VerifyScanQueue.pendingUnits()) — or
 * per stock balance (VerifyScanQueue.pendingLotUnits()).
 */
export type VerifyPendingUnits = ReadonlyMap<string, number>;

const NO_PENDING_UNITS: VerifyPendingUnits = new Map();

export type VerifyScanTriageInput = {
  /** A scan (camera / hardware / SharedScanner manual shape)… */
  scan?: VerifyScanLike | null;
  /** …or a unit the page's manual-entry form already built (manualPickVerifyUnit). */
  unit?: PickVerifyUnitInput | null;
  source: VerifyScanSource;
  mode: VerifyMode;
  groups: readonly VerifyGroupLike[];
  /** Dedupe keys of Add jobs queued or in flight (VerifyScanQueue.pendingKeys()). */
  pendingKeys: ReadonlySet<string>;
  /** Units queued or in flight per group — what the over-scan rule counts on top of `scanned`. */
  pendingUnits?: VerifyPendingUnits;
  /** …and per stock balance — what rule 7 counts on top of each lot's `available` / `verified`. */
  pendingLotUnits?: VerifyPendingUnits;
  /** The manual-entry form is open: page scans are ignored while the operator types. */
  manualEntryOpen?: boolean;
  /** A confirm / completion is on screen: scans are ignored, the camera window still slides. */
  paused?: boolean;
  now: number;
};

const SERIALIZED = 'SERIALIZED';

const trimmed = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

/** Same product key, serials equal under exact-first / case-insensitive scan policy. */
const verifyScanKeysMatch = (a: string, b: string): boolean => {
  if (a === b) return true;
  const splitA = a.split('|');
  const splitB = b.split('|');
  if (splitA.length < 2 || splitB.length < 2) return a.toLowerCase() === b.toLowerCase();
  const serialA = splitA.pop() || '';
  const serialB = splitB.pop() || '';
  return splitA.join('|') === splitB.join('|') && serialA.toLowerCase() === serialB.toLowerCase();
};

const matchUniqueVerifyScanKey = <T>(
  candidates: readonly T[],
  key: string,
  getKey: (candidate: T) => string
): T | null => {
  const exact = candidates.filter((candidate) => getKey(candidate) === key);
  if (exact.length === 1) return exact[0];
  if (exact.length > 1) return null;
  const loose = candidates.filter((candidate) => verifyScanKeysMatch(getKey(candidate), key));
  return loose.length === 1 ? loose[0] : null;
};

/**
 * Where a read came from. A SharedScanner manual object (`isManualEntry`) is `manual`; a scan with
 * no recognisable source is treated as a camera read — the conservative choice, since only camera
 * reads are subject to the re-read window.
 */
export const verifyScanSource = (
  scan: VerifyScanLike | null | undefined
): VerifyScanSource => {
  if (scan?.isManualEntry === true) return 'manual';
  return scan?.source === 'hardware' ? 'hardware' : 'camera';
};

/** The GTIN middle-12 a unit names (its `gtin` first, else the retail digits of its `code`). */
export const verifyUnitMiddle12 = (
  unit: PickVerifyUnitInput | null | undefined
): string | null =>
  scanGtinMiddle12({ gtin: unit?.gtin ?? null, text: unit?.code ?? '' });

/** Units one read adds or takes off: its typed quantity, else 1 — what the server counts. */
export const verifyUnitQuantity = (
  unit: PickVerifyUnitInput | null | undefined
): number => {
  const quantity = unit?.quantity;
  return typeof quantity === 'number' && Number.isInteger(quantity) && quantity >= 1
    ? quantity
    : 1;
};

/**
 * `middle12|serial` — the identity of one read. A code that is no product barcode at all (a typed
 * NDC, a zone label scanned by mistake) keys on its text so a re-read of it is still recognised.
 */
export const verifyScanKey = (unit: PickVerifyUnitInput | null | undefined): string => {
  const middle12 = verifyUnitMiddle12(unit);
  const serial = trimmed(unit?.serialNumber);
  return `${middle12 ?? `code:${trimmed(unit?.code) || trimmed(unit?.gtin)}`}|${serial}`;
};

/**
 * The group a middle-12 belongs to, by the server's own `matchKeys`. Add mode matches only groups
 * the order asks for — the server's scan never resolves to an orphan group, so neither does this
 * (an orphan match would otherwise skip the not-on-order refusal). Remove mode includes orphans:
 * taking their units off is the whole point of listing them.
 */
export const resolveVerifyGroup = <G extends VerifyGroupLike>(
  middle12: string | null,
  groups: readonly G[],
  { includeOrphans = false }: { includeOrphans?: boolean } = {}
): G | null => {
  if (!middle12) return null;
  return (
    groups.find(
      (group) =>
        (includeOrphans || !group.orphan) && (group.matchKeys || []).includes(middle12)
    ) ?? null
  );
};

const groupName = (group: VerifyGroupLike) =>
  trimmed(group.productName) || 'this product';

/**
 * Whether the device may count a unit towards its group: a SERIALIZED group only with a serial —
 * without one the server refuses the Add, and only it knows whether it can Remove one (rule 4).
 */
const countsTowards = (group: VerifyGroupLike, serial: string) =>
  !!serial || group.kind !== SERIALIZED;

export const verifyDuplicateSerialMessage = (serial: string, name: string) =>
  `Serial ${serial} (${name}) is already verified on this order.`;

/**
 * Mirror of the server's refusal (`VERIFY_CONTROLLED_LINE_LOCKED_MESSAGE`, libs/services
 * pick-verify/controlled.ts) for a read the device refuses itself (rule 2b).
 */
export const verifyControlledLockedMessage = (name: string, deaSchedule?: string | null) =>
  `${name} is a controlled substance (${
    (typeof deaSchedule === 'string' && deaSchedule.trim()) || 'controlled'
  }). Only a user with the "Fulfill Controlled Sales Order" permission can verify it.`;

/** The rule-2b refusal of a read of `group`. */
const lockedDecision = (group: VerifyGroupLike): VerifyScanDecision => ({
  type: 'locked',
  groupKey: group.key,
  title: 'Controlled product',
  message: verifyControlledLockedMessage(groupName(group), group.deaSchedule)
});

export const verifyScanTwoDHint = (name: string) =>
  `Scan the 2D barcode on ${name} — its serial must be captured.`;

const ignore = (reason: VerifyIgnoreReason, state: VerifyScanGateState) => ({
  decision: { type: 'ignore', reason } as VerifyScanDecision,
  state
});

/**
 * The lots a chooser offers in `mode` (rule 7), as the operator sees them while confirmations save
 * — each lot's units still saving moved from `available` to `verified` (a Remove the other way):
 * Add → the balances with units available, Remove → the lots with units verified. In the server's
 * order: its suggested row first, then FEFO.
 */
export const verifyLotOptions = <L extends VerifyLotLike>(
  group: { lots?: readonly L[] | null },
  mode: VerifyMode,
  pendingLotUnits: VerifyPendingUnits = NO_PENDING_UNITS
): L[] =>
  (group.lots || [])
    .map((lot) => {
      const saving = pendingLotUnits.get(lot.balanceId) || 0;
      return saving
        ? { ...lot, available: lot.available - saving, verified: lot.verified + saving }
        : lot;
    })
    .filter((lot) => (mode === 'add' ? lot.available : lot.verified) > 0);

const DAY_MS = 24 * 60 * 60 * 1000;
const ymdDay = (ymd: string): number | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  return match ? Date.UTC(+match[1], +match[2] - 1, +match[3]) / DAY_MS : null;
};

/**
 * The one row the label names: the lot it carries (and, when it carries an expiration too, within a
 * day of the row's — the server's own tolerance for stored instants), when exactly one row has it.
 * A GS1 case label says which lot is in hand; a lot in two zones does not say which zone.
 */
export const verifyLotLabelMatch = <L extends VerifyLotLike>(
  options: readonly L[],
  unit: PickVerifyUnitInput
): L | null => {
  const lot = trimmed(unit.lot);
  if (!lot) return null;
  const scannedDay = ymdDay(trimmed(unit.expiration));
  const named = options.filter((option) => {
    if (trimmed(option.lot) !== lot) return false;
    if (scannedDay === null) return true;
    const rowDay = ymdDay(formatDateOnly(option.expiration) || '');
    return rowDay !== null && Math.abs(rowDay - scannedDay) <= 1;
  });
  return named.length === 1 ? named[0] : null;
};

/** The row a chooser opens on: the row the label names, else the server's suggested row, else the first. */
export const verifyLotPreselect = (
  options: readonly VerifyLotLike[],
  unit: PickVerifyUnitInput
): string | null =>
  verifyLotLabelMatch(options, unit)?.balanceId ??
  (options.find((option) => option.suggested) ?? options[0])?.balanceId ??
  null;

/**
 * The job one chooser confirmation queues: the read with the balance and the units the operator
 * attributed to it. Never held back as a re-read or a double tap (rule 7): no dedupe key, and
 * `manual` — a tap on the page's own sheet, which plays no Scandit beep (nothing was decoded).
 */
export const verifyLotChoiceJob = (
  choice: VerifyLotChoice,
  balanceId: string,
  quantity: number
): VerifyScanJob => ({
  dedupeKey: null,
  mode: choice.mode,
  unit: { ...choice.unit, balanceId, quantity },
  groupKey: choice.groupKey,
  source: 'manual'
});

/**
 * Whether a chooser has done its job after a confirmation, counting what is still saving. Add: the
 * line needs nothing more — a mixed box is one confirmation per lot, so it stays open until then.
 * Remove: the line is back within what it needs (an over-scan undone; taking one unit off a line
 * that was not over is a single confirmation). Either way, also once no lot is left to choose or
 * the line is gone.
 */
export const verifyLotChoiceDone = (
  choice: VerifyLotChoice,
  group: VerifyGroupLike | null | undefined,
  pendingUnits: VerifyPendingUnits,
  pendingLotUnits: VerifyPendingUnits
): boolean => {
  if (!group) return true;
  const scanned = (group.scanned || 0) + (pendingUnits.get(group.key) || 0);
  const required = group.required || 0;
  const settled = choice.mode === 'add' ? scanned >= required : scanned <= required;
  return settled || verifyLotOptions(group, choice.mode, pendingLotUnits).length === 0;
};

/** A lot chooser on screen: its read, and how many of the read's units were already confirmed. */
export type VerifyOpenLotChoice = { choice: VerifyLotChoice; confirmed: number };

/** What a read landing while a lot chooser is open does instead of its triage decision. */
export type VerifyReadDuringLotChoice =
  /** Act on the triage decision as usual (another product, or a lot the label settles). */
  | { kind: 'asIs' }
  /** Another read of the chooser's own line: its units wait in the chooser for a lot. */
  | { kind: 'addToChoice'; quantity: number }
  | { kind: 'refuse'; title: string; message: string };

/**
 * A read while the chooser is open (rule 7). Scans are not paused for it — dropping a read
 * unheard is how units get lost — so: a read the triage would ask about for the SAME line and mode
 * adds its units to the chooser (bounded, in Add mode, by what the line still needs on top of the
 * units already waiting there); one that would open a chooser for ANOTHER line is refused aloud
 * (one chooser at a time); everything else — counted reads, duplicates, over-scans — as usual.
 */
export const verifyReadDuringLotChoice = (
  open: VerifyOpenLotChoice,
  decision: VerifyScanDecision,
  group: VerifyGroupLike | null | undefined,
  pendingUnits: VerifyPendingUnits = NO_PENDING_UNITS
): VerifyReadDuringLotChoice => {
  if (decision.type !== 'choose-lot') return { kind: 'asIs' };
  const { choice } = open;
  const name = groupName(
    group ?? { key: choice.groupKey, kind: '', required: 0, scanned: 0 }
  );
  if (
    decision.choice.groupKey !== choice.groupKey ||
    decision.choice.mode !== choice.mode
  ) {
    return {
      kind: 'refuse',
      title: 'Choose the lot first',
      message: `Choose the lot first — confirm or close the list for ${name}. That scan was not counted.`
    };
  }
  const quantity = decision.choice.quantity;
  if (choice.mode === 'add' && group) {
    const waiting = Math.max(0, choice.quantity - open.confirmed);
    const remaining =
      (group.required || 0) -
      (group.scanned || 0) -
      (pendingUnits.get(group.key) || 0) -
      waiting;
    if (quantity > remaining) {
      const message = verifyOverScanMessage(
        group.required || 0,
        Math.max(0, remaining),
        name
      );
      return { kind: 'refuse', title: verifyRefusalTitle(message, 'add'), message };
    }
  }
  return { kind: 'addToChoice', quantity };
};

/** (7) A counted BULK / UPC unit: sent as it is, sent with its only lot, or the lot chooser. */
const withLot = (
  job: VerifyScanJob,
  group: VerifyGroupLike,
  pendingLotUnits: VerifyPendingUnits | undefined,
  state: VerifyScanGateState
): { decision: VerifyScanDecision; state: VerifyScanGateState } => {
  const options =
    group.kind === SERIALIZED || job.unit.balanceId
      ? []
      : verifyLotOptions(group, job.mode, pendingLotUnits);
  if (options.length === 0) return { decision: { type: 'enqueue', job }, state };
  const units = (lot: VerifyLotLike) =>
    job.mode === 'add' ? lot.available : lot.verified;
  const labelled = options.length > 1 ? verifyLotLabelMatch(options, job.unit) : null;
  const only =
    options.length === 1
      ? options[0]
      : labelled && units(labelled) >= verifyUnitQuantity(job.unit)
      ? labelled
      : null;
  if (only) {
    const unit = { ...job.unit, balanceId: only.balanceId };
    return { decision: { type: 'enqueue', job: { ...job, unit } }, state };
  }
  return {
    decision: {
      type: 'choose-lot',
      choice: {
        mode: job.mode,
        groupKey: group.key,
        unit: job.unit,
        source: job.source,
        balanceId: verifyLotPreselect(options, job.unit),
        quantity: verifyUnitQuantity(job.unit)
      }
    },
    state
  };
};

/**
 * Decide what one read does (see the header for the rules and why). Pure: the caller holds the
 * returned state and passes it back with the next read.
 */
export function triageVerifyScan(
  input: VerifyScanTriageInput,
  state: VerifyScanGateState
): { decision: VerifyScanDecision; state: VerifyScanGateState } {
  const { source, mode, groups, pendingKeys, now } = input;
  const unit: PickVerifyUnitInput = input.unit
    ? { ...input.unit }
    : toPickVerifyUnit(input.scan ?? null);
  if (!trimmed(unit.code) && !trimmed(unit.gtin) && !trimmed(unit.serialNumber)) {
    return ignore('empty', state);
  }
  const paused = !!input.paused || (!!input.manualEntryOpen && source !== 'manual');
  const middle12 = verifyUnitMiddle12(unit);
  const serial = trimmed(unit.serialNumber);

  // (1) Remove: every read goes to the server as read — no in-flight, no duplicate — except a
  // camera re-read of the unit still in frame, which is ignored exactly as in Add mode. The anchor
  // is mode-prefixed so an Add read never masks the first Remove read of the same unit.
  if (mode === 'remove') {
    let removeState = state;
    if (source === 'camera') {
      const removeKey = `${REMOVE_KEY_PREFIX}${verifyScanKey(unit)}`;
      const reread = cameraStillSees(state, removeKey, now);
      removeState = withCameraRead(state, removeKey, now);
      if (paused) return ignore('paused', removeState);
      if (reread) return ignore('camera-reread', removeState);
    } else if (paused) {
      return ignore('paused', state);
    }
    const group = resolveVerifyGroup(middle12, groups, { includeOrphans: true });
    if (group?.controlledLocked) return { decision: lockedDecision(group), state: removeState }; // (2b)
    const removeJob: VerifyScanJob = {
      dedupeKey: null,
      mode: 'remove',
      unit,
      groupKey: group && countsTowards(group, serial) ? group.key : null,
      source
    };
    if (!group || !removeJob.groupKey) {
      return { decision: { type: 'enqueue', job: removeJob }, state: removeState };
    }
    return withLot(removeJob, group, input.pendingLotUnits, removeState); // (7)
  }

  // (2) Add: silent suppression of the unit still in frame / already on its way.
  const key = verifyScanKey(unit);
  let next = state;
  let reread = false;
  if (source === 'camera') {
    reread = cameraStillSees(state, key, now);
    // Sliding: the window is refreshed by EVERY camera read, suppressed or not (while a confirm or
    // the entry form is open too), so a pack held in frame never ages out of it.
    next = withCameraRead(state, key, now);
  }
  if (paused) return ignore('paused', next);
  if (reread) return ignore('camera-reread', next);
  // Only a key that names one unit can already be on its way (see rule 2): past the window, a
  // serial-less read is the next bottle of the product.
  const namesOneUnit = !!serial || source === 'manual';
  if (namesOneUnit && matchUniqueVerifyScanKey([...pendingKeys], key, (pending) => pending)) {
    return ignore('in-flight', next);
  }

  const group = resolveVerifyGroup(middle12, groups);
  const job: VerifyScanJob = {
    dedupeKey: key,
    mode: 'add',
    unit,
    groupKey: group?.key ?? null,
    source
  };
  if (!group) return { decision: { type: 'enqueue', job }, state: next }; // (6)
  if (group.controlledLocked) return { decision: lockedDecision(group), state: next }; // (2b)

  // (3) Duplicate serial — answered on the spot (exact or unique case-insensitive). An ambiguous
  // case-only collision falls through to the server, which refuses rather than guessing.
  const duplicateSerial =
    serial &&
    matchUniqueSerialCaseInsensitive(group.serialNumbers || [], serial, (value) => value);
  if (duplicateSerial) {
    return {
      decision: {
        type: 'duplicate',
        groupKey: group.key,
        serialNumber: duplicateSerial,
        title: 'Duplicate serial',
        message: verifyDuplicateSerialMessage(duplicateSerial, groupName(group))
      },
      state: next
    };
  }

  // (4) The linear code on a serialized pack — a nudge, not a refusal.
  if (
    group.kind === SERIALIZED &&
    source !== 'manual' &&
    isRetailCodeScan(input.scan ?? null)
  ) {
    if (next.lastHintAt != null && now - next.lastHintAt < VERIFY_SOFT_HINT_INTERVAL_MS) {
      return ignore('hint-rate-limited', next);
    }
    return {
      decision: {
        type: 'hint',
        groupKey: group.key,
        title: 'Scan the 2D barcode',
        message: verifyScanTwoDHint(groupName(group))
      },
      state: { ...next, lastHintAt: now }
    };
  }
  // …and any other unit without a serial for it: the server's to count, or refuse.
  if (!countsTowards(group, serial)) {
    return {
      decision: { type: 'enqueue', job: { ...job, groupKey: null } },
      state: next
    };
  }

  // (5) Over-scan — refused exactly where the server would refuse it, but counting what is still
  // saving: the operator has already heard those units counted.
  const remaining =
    (group.required || 0) -
    (group.scanned || 0) -
    ((input.pendingUnits ?? NO_PENDING_UNITS).get(group.key) || 0);
  if (verifyUnitQuantity(unit) > remaining) {
    const message = verifyOverScanMessage(
      group.required || 0,
      remaining,
      groupName(group)
    );
    return {
      decision: {
        type: 'over-scan',
        groupKey: group.key,
        title: verifyRefusalTitle(message, 'add'),
        message
      },
      state: next
    };
  }

  return withLot(job, group, input.pendingLotUnits, next); // (7)
}

// ─────────────────────────────────────── serial queue ───────────────────────────────────────

/**
 * What became of one job of a batch (VerifyScanQueueOptions.runBatch), in the order sent: its
 * result, its refusal / failure, or `retry` — not tried by the server (after an earlier job's refusal,
 * or cut off by a failure later in the batch), so it goes back to the front of the queue.
 */
export type VerifyBatchOutcome<R> =
  | { kind: 'result'; result: R }
  | { kind: 'error'; error: unknown }
  | { kind: 'retry' };

/**
 * Add jobs per batch: what piles up behind a request in flight. Bounded so the batch's transaction
 * stays short — a long one holds its stock rows longer, and another device picking the same product
 * then waits and retries. Past ~5 units the saving per unit is small (the shared reads are spread).
 */
export const VERIFY_SCAN_BATCH_SIZE = 5;

export type VerifyScanQueueOptions<R> = {
  /** Runs one job (the Verify scan / remove mutation). Never called concurrently. */
  run: (job: VerifyScanJob) => Promise<R>;
  /**
   * Runs the Add jobs at the head of the queue in ONE request (salesOrderVerifyScanBatch), at most
   * `maxBatch` of them; resolves with one outcome per job, in order. A rejection is the FIRST job's
   * failure (the others go back on the queue). Optional: without it every job goes through `run`.
   * Never called concurrently with itself or `run`.
   */
  runBatch?: (jobs: VerifyScanJob[]) => Promise<VerifyBatchOutcome<R>[]>;
  maxBatch?: number;
  /**
   * For a request that failed WITHOUT an answer — it may or may not have been applied — how long to
   * wait before sending its jobs again (same jobs, same ids, still at the front, nothing delivered);
   * null to deliver the failure as usual. Asked with the request's first job.
   */
  resendDelayFor?: (error: unknown, job: VerifyScanJob) => number | null;
  onResult?: (result: R, job: VerifyScanJob) => void;
  onError?: (error: unknown, job: VerifyScanJob) => void;
  /**
   * Queued + in-flight count, units per group and per stock balance, on every change (drives the
   * "Saving…" line, Complete, the counts the rows show before the server answers, and what the lot
   * chooser says each lot still holds).
   */
  onPendingChange?: (
    pending: number,
    units: VerifyPendingUnits,
    lotUnits: VerifyPendingUnits
  ) => void;
};

export type VerifyScanQueue = {
  /** Queues a job; it is given a `clientScanId` here when it has none (see newVerifyClientScanId). */
  enqueue: (job: VerifyScanJob) => void;
  /** Dedupe keys of every Add job queued or in flight. */
  pendingKeys: () => Set<string>;
  /**
   * Net units of every job queued or in flight, per group the client resolved: an Add counts
   * its units, a Remove takes them off. Jobs with no group are left out — only the server can
   * say what they count towards.
   */
  pendingUnits: () => Map<string, number>;
  /**
   * The same, per stock balance (`unit.balanceId`). Jobs sent without one are left out — the
   * server picks their balance.
   */
  pendingLotUnits: () => Map<string, number>;
  pending: () => number;
  /**
   * Drop every queued job and disown the ones in flight (their outcomes are never delivered). Used
   * when the order stops scanning: documents panel, lost order, back in Pick, the split.
   */
  drain: () => void;
  /**
   * Keep sending every job already queued, but deliver nothing any more — no outcome, no pending
   * notice. Used when the screen goes away: the scans it already counted (tone and row) are still
   * recorded, and nothing lands on a screen that no longer exists.
   */
  detach: () => void;
};

/**
 * The id a Verify write is sent with (`clientScanId`): the server records it with the write, so a
 * request re-sent after a timeout is answered instead of picking the units again. Random, never
 * reused: a read scanned twice on purpose is two scans with two ids.
 */
export function newVerifyClientScanId(): string {
  const random =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID().replace(/-/g, '')
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random()
          .toString(36)
          .slice(2)}`;
  return `v${random}`.slice(0, 40);
}

/**
 * One request at a time, in the order the reads arrived. Each Verify write touches the same order
 * document, so parallel writes would only conflict and retry server-side; serial writes also make
 * "newest list wins" trivially true for the lists they return. With `runBatch`, the Add jobs that
 * piled up behind the request in flight go together in the next one (a Remove is always sent alone,
 * and in its place in the line), and every outcome is delivered in arrival order — exactly as if they
 * had been sent one by one.
 */
export function createVerifyScanQueue<R>({
  run,
  runBatch,
  maxBatch = VERIFY_SCAN_BATCH_SIZE,
  resendDelayFor,
  onResult,
  onError,
  onPendingChange
}: VerifyScanQueueOptions<R>): VerifyScanQueue {
  const queued: VerifyScanJob[] = [];
  let active: VerifyScanJob[] = [];
  // Set while waiting to re-send jobs that got no answer: nothing is sent before it fires.
  let resendTimer: ReturnType<typeof setTimeout> | null = null;
  // Bumped by drain(): jobs started before a drain deliver nothing when they settle.
  let generation = 0;
  // Set by detach(): the queue keeps sending, and delivers nothing to anyone.
  let detached = false;

  const pending = () => queued.length + active.length;
  const pendingJobs = () => (active.length ? [...active, ...queued] : queued);
  const netUnits = (keyOf: (job: VerifyScanJob) => string | null | undefined) => {
    const units = new Map<string, number>();
    for (const job of pendingJobs()) {
      const key = keyOf(job);
      if (!key) continue;
      const quantity = verifyUnitQuantity(job.unit);
      const delta = job.mode === 'remove' ? -quantity : quantity;
      units.set(key, (units.get(key) || 0) + delta);
    }
    return units;
  };
  const pendingUnits = () => netUnits((job) => job.groupKey);
  const pendingLotUnits = () => netUnits((job) => job.unit.balanceId);
  const notify = () => {
    if (!detached) onPendingChange?.(pending(), pendingUnits(), pendingLotUnits());
  };

  /**
   * Deliver every outcome BEFORE the jobs leave the pending set: the caller applies the returned list
   * (with the new serials in it) first, so there is no instant at which a re-read of one is neither
   * in flight nor in the list. The pending notice right after, in the same tick, takes their units out
   * of the optimistic counts — the page renders the two together. Jobs to `retry` go back to the
   * front of the queue, in order — unless the queue was drained meanwhile (they are dropped with it).
   */
  const settle = (
    jobs: VerifyScanJob[],
    startedIn: number,
    outcomes: VerifyBatchOutcome<R>[]
  ) => {
    const again: VerifyScanJob[] = [];
    jobs.forEach((job, index) => {
      let outcome = outcomes[index] ?? { kind: 'retry' };
      // The first job is never sent back: that could resend it forever. A batch that tried nothing
      // (a server answer this client cannot read) is that job's failure.
      if (index === 0 && outcome.kind === 'retry') {
        outcome = {
          kind: 'error',
          error: new Error('The scan could not be saved.')
        };
      }
      if (outcome.kind === 'retry') {
        again.push(job);
        return;
      }
      if (startedIn !== generation || detached) return;
      try {
        if (outcome.kind === 'result') onResult?.(outcome.result, job);
        else onError?.(outcome.error, job);
      } catch (e) {
        console.error('verify queue handler error:', e);
      }
    });
    active = [];
    if (startedIn === generation) queued.unshift(...again);
    notify();
    pump();
  };

  /** The jobs got no answer: back to the front, unchanged, and sent again after `delayMs`. */
  const resendLater = (jobs: VerifyScanJob[], startedIn: number, delayMs: number) => {
    active = [];
    if (startedIn !== generation) {
      // Drained meanwhile: they go with everything else.
      notify();
      pump();
      return;
    }
    queued.unshift(...jobs);
    notify();
    resendTimer = setTimeout(() => {
      resendTimer = null;
      pump();
    }, delayMs);
  };

  const pump = () => {
    if (active.length || resendTimer || queued.length === 0) return;
    const startedIn = generation;
    let size = 1;
    if (runBatch && queued[0].mode === 'add') {
      while (size < queued.length && size < maxBatch && queued[size].mode === 'add') size += 1;
    }
    const jobs = queued.splice(0, size);
    active = jobs;
    const batched = !!runBatch && jobs[0].mode === 'add';
    let promise: Promise<VerifyBatchOutcome<R>[]>;
    try {
      promise = batched
        ? Promise.resolve(runBatch!(jobs))
        : Promise.resolve(run(jobs[0])).then((result) => [
            { kind: 'result', result } as VerifyBatchOutcome<R>
          ]);
    } catch (e) {
      promise = Promise.reject(e);
    }
    promise.then(
      (outcomes) => settle(jobs, startedIn, outcomes),
      (error) => {
        const delayMs = resendDelayFor?.(error, jobs[0]) ?? null;
        if (delayMs != null) return resendLater(jobs, startedIn, delayMs);
        settle(jobs, startedIn, [
          { kind: 'error', error },
          ...jobs.slice(1).map(() => ({ kind: 'retry' }) as VerifyBatchOutcome<R>)
        ]);
      }
    );
  };

  return {
    enqueue: (job) => {
      queued.push(job.clientScanId ? job : { ...job, clientScanId: newVerifyClientScanId() });
      notify();
      pump();
    },
    pendingKeys: () => {
      const keys = new Set<string>();
      for (const job of pendingJobs()) {
        if (job.dedupeKey) keys.add(job.dedupeKey);
      }
      return keys;
    },
    pendingUnits,
    pendingLotUnits,
    pending,
    drain: () => {
      generation += 1;
      queued.length = 0;
      if (resendTimer) {
        clearTimeout(resendTimer);
        resendTimer = null;
      }
      notify();
    },
    detach: () => {
      detached = true;
    }
  };
}

// ─────────────────────────────────────── list merge ───────────────────────────────────────

export type VerifyListLike<L extends { key: string } = { key: string }> = {
  lines: readonly L[];
};

/**
 * The incoming list, with its rows kept in the order the operator already sees: groups present
 * before keep their position, new groups (an orphan that just appeared) are appended in the
 * server's order, groups that are gone are dropped. Rows that jump while someone is scanning are
 * rows that get misread.
 */
export function mergeVerifyList<T extends VerifyListLike<any>>(
  previous: T | null | undefined,
  next: T
): T {
  if (!previous?.lines?.length) return next;
  const position = new Map<string, number>(
    previous.lines.map((line, i) => [line.key, i])
  );
  const known = next.lines.filter((line) => position.has(line.key));
  const added = next.lines.filter((line) => !position.has(line.key));
  known.sort((a, b) => (position.get(a.key) as number) - (position.get(b.key) as number));
  return { ...next, lines: [...known, ...added] };
}

/**
 * The list as the operator sees it while scans are saving: each group's `scanned` moved by its net
 * pending units (VerifyScanQueue.pendingUnits), and `totalScanned` with it. A read is counted the
 * moment it is accepted, not a round trip later; the server's next list replaces these numbers,
 * and a refusal rolls them back simply by the job leaving the queue.
 */
export function withPendingUnits<
  T extends VerifyListLike<{ key: string; scanned: number }> & {
    totalScanned?: number | null;
  }
>(list: T, units: VerifyPendingUnits): T {
  if (!units.size) return list;
  let moved = 0;
  const lines = list.lines.map((line) => {
    const delta = units.get(line.key) || 0;
    if (!delta) return line;
    // A Remove never shows fewer than none; the server refuses it anyway.
    const scanned = Math.max(0, line.scanned + delta);
    moved += scanned - line.scanned;
    return { ...line, scanned };
  });
  return { ...list, lines, totalScanned: (list.totalScanned || 0) + moved };
}

export type VerifyListSnapshot<T> = { seq: number; list: T };

/**
 * Newest list wins. Every request that returns a list (the load, a refetch, each queued mutation)
 * takes a sequence number when it is SENT; a response older than the one on screen is dropped —
 * e.g. a refetch sent before a scan but answered after it.
 */
export function nextVerifyListSnapshot<T extends VerifyListLike<any>>(
  current: VerifyListSnapshot<T> | null,
  incoming: VerifyListSnapshot<T>
): VerifyListSnapshot<T> {
  if (current && incoming.seq < current.seq) return current;
  return { seq: incoming.seq, list: mergeVerifyList(current?.list, incoming.list) };
}
